/**
 * RSoC ad-type handler — Related Search on Content.
 *
 * Reads `page_options.rsoc` and drives the existing `google-rsoc` machinery,
 * which calls `_googCsa("relatedsearch", ...)`. This is the path ramp.js has
 * always run on publisher pages. Behaviour is a verbatim extraction of the
 * previous module's `initialize`:
 *
 *   googleRsoc.initAdCallbackTracking(server, AD_CALLBACK_TIMEOUT);
 *   googleRsoc.onResponse(server);
 *
 * Order matters: initAdCallbackTracking stamps `__wrappedAdLoadedCallback` onto
 * each unit's params, which onResponse then consumes. google-rsoc owns its own
 * beacon-cookie stamping (see google-rsoc.onResponse); the shared `beacons.ts`
 * helper is used by the CSA handler, which had no such loop of its own.
 */

import type { Response } from "@ascent/papi-core";
import type { Response as ServerResponse } from "@rampjs/server";
import * as googleRsoc from "../../google-rsoc";

/** Configurable ad-callback timeout (ms), preserved from main.ts. */
const AD_CALLBACK_TIMEOUT =
  (window as { RSOC_AD_CALLBACK_TIMEOUT?: number }).RSOC_AD_CALLBACK_TIMEOUT ||
  10000;

/**
 * Reconstruct the server envelope the google-rsoc functions expect. They only
 * read `server.search.response.*`, so wrapping the same response object is
 * equivalent to passing the original envelope — and mutations (e.g.
 * `__wrappedAdLoadedCallback`) persist across both calls since it is the same
 * object reference.
 */
function toServerEnvelope(response: Response): ServerResponse {
  return { search: { response } } as ServerResponse;
}

/**
 * True when the response carries RSoC page options. Drives whether the module
 * runs this handler.
 */
export function hasRsoc(response: Response): boolean {
  return !!response.page_options?.rsoc;
}

/**
 * Run the RSoC path. No-op deferral is handled inside google-rsoc.onResponse
 * (it early-returns when `page_options.rsoc` is absent), but callers should
 * gate on `hasRsoc` to keep the two ad types independent.
 */
export function initializeRsoc(response: Response): void {
  const server = toServerEnvelope(response);
  // Order matters: set up callback tracking before onResponse consumes it.
  googleRsoc.initAdCallbackTracking(server, AD_CALLBACK_TIMEOUT);
  googleRsoc.onResponse(server);
}
